import { runInAction, toJS, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFilter } from 'react-select-modern';

import { EUserRoleIDs, type UserRole } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { toast, UniversalSelect } from '@feathr/components';
import { useAccount, useRole, useStore, useUser } from '@feathr/extender/state';
import { errorMessage } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

export interface IRoleSelectProps {
  /** ID to disable from the options menu. */
  disableById?: string;
  /** ID to exclude from the options menu. */
  excludeById?: string;
  helpText?: ReactNode;
  label?: ReactNode;
  includeGlobalRoles?: boolean;
  menuPortalTarget?: HTMLElement;
  userId?: string;
  value?: string;
  onChange?: (id: string) => void;
}

/** Without this the modern select falls back to `JSON.stringify(option)` for a blank role name. */
function getRoleOptionLabel(option: ISelectOption): string {
  return option.name;
}

const matchesInput = createFilter<ISelectOption>({ stringify: ({ label }): string => label });

function RoleSelect({
  disableById,
  helpText,
  excludeById,
  includeGlobalRoles = false,
  label,
  userId,
  value,
  onChange,
  ...additionalProps
}: Readonly<IRoleSelectProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const { UserRoles } = useStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [roleOptions, setRoleOptions] = useState<ISelectOption[]>([]);
  const { id: currentUserId } = useUser();
  const userRoles = account.get('user_roles');
  const { isInternalOnboarding } = useRole();

  function convertModelToOptions(roles: ListResponse<UserRole>): ISelectOption[] {
    return roles.models.map(({ id, name }: UserRole): ISelectOption => ({ id: id, name: name }));
  }

  async function getRoleOptions(): Promise<ISelectOption[]> {
    if (roleOptions.length > 0) {
      return roleOptions;
    }
    setLoading(true);
    try {
      const roleOptions: ISelectOption[] = [];
      const roles = UserRoles.list();
      await when(() => !roles.isPending);
      roleOptions.push(...convertModelToOptions(roles));
      if (includeGlobalRoles && isInternalOnboarding) {
        roleOptions.push(
          // When on account users settings page, don't allow changing existing roles to these
          { id: EUserRoleIDs.Accountant, name: t('Accountant') },
          { id: EUserRoleIDs.AccountManager, name: t('Account Manager') },
          { id: EUserRoleIDs.Pro, name: t('Pro') },
        );
        // Sort roles that start with 000000000000000000000 in place alphabetically
        // by name; everything else stays in the same order
        roleOptions.sort((a: ISelectOption, b: ISelectOption): number => {
          // Sort roles that start with 000000000000000000000 first
          const aIsDefinedRole = a.id.startsWith('000000000000000000000');
          const bIsDefinedRole = b.id.startsWith('000000000000000000000');
          if (aIsDefinedRole && bIsDefinedRole) {
            return a.name.localeCompare(b.name);
          }
          if (aIsDefinedRole && !bIsDefinedRole) {
            return -1;
          }
          if (!aIsDefinedRole && bIsDefinedRole) {
            return 1;
          }
          // Do not change order
          return 0;
        });
      }
      setRoleOptions(roleOptions);
      return roleOptions;
    } catch {
      toast(t("Couldn't load roles. Try again."), { type: 'error' });
      return [];
    } finally {
      setLoading(false);
    }
  }

  async function updateUserRole({ id: roleId }: ISelectOption): Promise<void> {
    if (userId) {
      runInAction((): void => {
        // TODO: FIX THIS!
        /*
        if this were a SYNC app, this wouldn't be an issue, but due to it being ASYNC
        1. user a changes permissions for user c, d, e, and submits, after user b loads the same page
        2. user b changes permissions for users e, f, g
        3. permissions changes for users c, d are reverted, and changes for user e are overridden
        */
        account.set({
          user_roles: [
            ...userRoles.filter((i) => i.user !== userId),
            { user: userId, role: roleId },
          ],
        });
      });

      try {
        const updatedAccount = await account.patchDirty();
        if (updatedAccount.isErrored) {
          toast(
            t("Couldn't update role. {{error}}", { error: errorMessage(updatedAccount.error, t) }),
            {
              type: 'error',
            },
          );
          return;
        }

        toast(t('Reassigned role.'), { type: 'success' });
      } catch (error) {
        toast(t("Couldn't update role. {{error}}", { error: errorMessage(error, t) }), {
          type: 'error',
        });
      }
    }
  }

  async function handleRoleSelected(selectedRole: ISelectOption): Promise<void> {
    const { id: newRoleId } = selectedRole;
    if (userId) {
      const currentRoleId = userRoles.find((i) => i.user === userId)?.role;
      if (newRoleId !== currentRoleId) {
        await updateUserRole(selectedRole);
      }
    }
    onChange?.(newRoleId);
  }

  function getValue(): ISelectOption | undefined {
    if (userId) {
      const userRole = toJS(userRoles.find((i) => i.user === userId));
      const defaultOption = roleOptions.find((option) => option.name === 'User');
      return roleOptions.find((option) => userRole?.role === option.id) ?? defaultOption;
    }
    return roleOptions.find(({ id }: ISelectOption): boolean => id === value);
  }

  /** Evaluated on every render, so a change to `disableById` reaches the menu immediately. */
  function isOptionDisabled({ id }: ISelectOption): boolean {
    return id === disableById;
  }

  function filterOption(option: ISelectOption, rawInput: string): boolean {
    return (
      option.id !== excludeById &&
      matchesInput({ data: option, label: getRoleOptionLabel(option), value: option.id }, rawInput)
    );
  }

  return (
    <UniversalSelect<ISelectOption>
      {...additionalProps}
      async={true}
      defaultOptions={true}
      disabled={currentUserId === userId}
      filterOption={filterOption}
      getOptionLabel={getRoleOptionLabel}
      helpText={helpText}
      isLoading={loading}
      isOptionDisabled={isOptionDisabled}
      label={label ?? t('Role')}
      loadOptions={getRoleOptions}
      name={'role-select'}
      onSelectSingle={handleRoleSelected}
      placeholder={t('Select a role...')}
      value={getValue()}
    />
  );
}

export default observer(RoleSelect);
